/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { Promise } from 'bluebird';
import classNames from 'classnames';
import fileExtension from 'file-extension';
import _ from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { TextField, DefaultButton } from '@bingads-webui-react/fabric-control';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { UploadProgress } from '@bingads-webui-campaign-react/upload-file-picker';
import { ProgressBar } from '@bingads-webui-campaign-react/image-experience';
import { MessageBar } from '@fluentui/react';
import { MultiVideoListView } from './multi-video-list-view';
import GenericImage from './add-video/media/placeholder-replace-static-img.png';
import { getReplaceVideoStyle } from '../style/js/replace-video-style';
import { AddVideoContainer } from './add-video';
import { VideoListView } from './video-list-view';
import { ImageExperienceVideo, newVideoFromMTObject } from './model/image-experience-video';
import { VideoRepository } from './util/video-repository';
import { VideoResourceRepository } from './util/video-resource-repository';
import { VideoSyncer } from './util/video-syncer';
import { FormatVideoError } from './util/format-video-error';
import { initializeUserVideoDataSource } from '../src/add-video/data-source/user-video-data-source';
import C from './constants';
import { EditVideo } from './edit-video';
import { handleFileRead } from './util/video-common';
import { CustomThumbnailRecordType } from './types';
import { testThumbnail } from './util/video-thumbnail-test';
import { getCCJsDataObj } from './util/ccui-util';
import { isValidXandrAccount } from './util/check-valid-xandr-account';

export class VideoExperienceComponent extends React.PureComponent {
  constructor(props) {
    super(props);
    this.isMountedFlag = false;

    this.isXandrIntEnabled = props.permissions && props.permissions.dynamic ? props.permissions.dynamic.XandrIntegrationEnabled : false;
    this.jsDataCcRest = getCCJsDataObj(props.cid, props.serverContext.CurrentUser.Id);

    this.defaultPreviewFields = {
      shortHeadline: props.i18n.getString('Audience_Campaign_Ad_Preview_Default_ShortHeadline'),
      longHeadline: props.i18n.getString('Audience_Campaign_Ad_Preview_Default_LongHeadline'),
      description: props.i18n.getString('Audience_Campaign_Ad_Preview_Default_Description'),
      businessName: props.i18n.getString('Audience_Campaign_Ad_Preview_Default_BusinessName'),
      url: 'https://www.bing.com',
    };

    this.videoRepository = new VideoRepository(props.cid, props.aid, props.oDataConfig, props.activityLogger);
    this.videoResRepository = new VideoResourceRepository(props.cid, props.aid, props.oDataConfig, props.activityLogger);
    this.videoSyncer = new VideoSyncer(this.videoRepository, this.props.activityLogger);
    this.userVideoDataSource = initializeUserVideoDataSource({
      videoRepository: this.videoRepository,
    });
    this.onClipped = this.onClipped.bind(this);
    this.state = {
      isVideoSelectorDisplayed: false,
      isEditVideoDisplayed: false,
      editingVideo: null,
      isVideoUploading: false,
      repairProgress: 0,
      repairMode: false,
      videoUploadedBytes: 0,
      videoFileUploading: null,
      uploadVideoAsyncToken: null,
      videoObjFromUI: {},
      errors: [],
      uploadVideoForReplace: null,
      isXandrIntegrationEnabled: false,
    };

    this.allowedVideoSizes = props.allowedVideoSizes || C.videoTypesWithOrder;
  }


  componentDidMount() {
    this.isMountedFlag = true;

    this.props.videoList.forEach((video) => {
      if (video.Status === 'Draft' || video.Status === 'Unverified') {
        this.syncVideo(video.IdString, video.Type);
      }
    });

    if (this.isXandrIntEnabled && !this.state.isXandrIntegrationEnabled) {
      this.fetchAccountData();
    }
  }

  componentDidUpdate() {
    if (this.props.videoList.length === 0) {
      this.videoSyncer.stopAll();
    }
  }

  componentWillUnmount() {
    this.videoSyncer.stopAll();
    this.isMountedFlag = false;
  }

  onAddVideo = () => {
    this.setState({
      isVideoSelectorDisplayed: true,
    });

    if (this.props.multiVideoMode) {
      return;
    }

    this.props.onVideoListUpdated([]);
  };

  onVideoSelected = (video) => {
    this.logToSubstrate('selected a video');
    this.setState({
      isVideoSelectorDisplayed: false,
      errors: [],
    });

    if (this.state.editingVideo) {
      this.replaceVideo(new ImageExperienceVideo({ ...video, type: this.state.editingVideo.Type }));
    } else if (this.props.multiVideoMode) {
      const newVideo = {
        ...video,
        IdString: video.idString || video.id || '',
      };

      const videos = [...this.props.videoList];
      const isDuplicate = videos.some(existingVideo => existingVideo.id === newVideo.id);

      if (!isDuplicate) {
        this.allowedVideoSizes.forEach((type) => {
          videos.push(new ImageExperienceVideo({ ...newVideo, type }));
        });

        this.props.onVideoListUpdated(videos);
      } else {
        return;
      }
    } else {
      const videos = [];
      this.allowedVideoSizes.forEach((type) => {
        videos.push(new ImageExperienceVideo({ ...video, type }));
      });
      this.props.onVideoListUpdated(videos);
    }
    // we clear this state to avoid cache issue
    this.setState({
      editingVideo: null,
      repairMode: false,
    });
  };

  // Thumbnail change is saved as draft and only takes effect when user clicks save in the ad editor
  onSaveThumbnail = ({
    thumbnailRecord,
  }) => {
    /* istanbul ignore next */
    this.props.onSaveThumbnailAsDraft({
      thumbnailRecord,
    });
    this.props.onVideoPreviewRequested(this.state.editingVideo.Type);
    this.setState({
      isEditVideoDisplayed: false,
    });
  };

  // Crop/clip/caption changes take effect immediately.
  onCropped = (response, type) => {
    /* istanbul ignore next */
    this.syncVideo(response.value, type);
    this.setState({
      isEditVideoDisplayed: false,
    });
  };

  onClipped = ({ value: idString }, type, { originalIdString, isApplyTrimToAllAspectRatio }) => {
    let typesTobeSynced = null;
    /* istanbul ignore next */
    if (isApplyTrimToAllAspectRatio) {
      typesTobeSynced = this.props.videoList
        .filter(video => video.IdString === originalIdString)
        .map(video => video.Type);
      this.syncVideoForMultipleTypes(idString, typesTobeSynced);
    } else {
      this.syncVideo(idString, type);
    }
    this.setState({
      isEditVideoDisplayed: false,
    });
  };

  onVideoSelectionCanceled = () => {
    this.setState({
      isVideoSelectorDisplayed: false,
      editingVideo: null,
      repairMode: false,
    });
  };

  onReplaceVideo = (type) => {
    const videoToEdit = _.find(this.props.videoList, video => video.Type === type);
    /* istanbul ignore if */
    if (!videoToEdit) {
      return;
    }
    this.setState({
      isVideoSelectorDisplayed: true,
      editingVideo: videoToEdit.clone(),
    });
  };

  onRepairVideo = () => {
    setClarityTag({ GoogleVideoImportVideoRepair: 'true' });

    this.logToSubstrateAdvanced('click repair button', 'video repair');
    this.setState({
      isVideoSelectorDisplayed: true,
      repairMode: true,
    });
  };

  onEditVideo = (type) => {
    this.logToSubstrate('edit a video');
    const videoToEdit = _.find(this.props.videoList, video => video.Type === type);

    /* istanbul ignore if */
    if (!videoToEdit) {
      return;
    }
    this.setState({
      isEditVideoDisplayed: true,
      editingVideo: videoToEdit.clone(),
    });
  };

  onRemoveAllVideos = () => {
    // eslint-disable-next-line
    if (this.state.uploadVideoForReplace && !confirm(this.props.i18n.getString('ImageExperience_Video_RemoveAllWithUpload_Confirm'))) {
      return;
    }
    /* istanbul ignore if */
    if (this.state.uploadVideoAsyncToken) {
      this.state.uploadVideoAsyncToken.cancel();
    }
    this.logToSubstrate('remove all videos');
    this.setState({
      editingVideo: null,
    });
    this.props.onVideoListUpdated([]);
  };

  onCancelUpload = () => {
    // eslint-disable-next-line
    if (confirm(this.props.i18n.getString('ImageExperience_Video_Upload_Cancel_Confirm')) &&
      this.state.uploadVideoAsyncToken) {
      this.state.uploadVideoAsyncToken.cancel();
    }
  };

  onProgress = (precentage) => {
    /* istanbul ignore if */
    if (this.state.repairMode) {
      this.onRepairProgress(precentage);
    }
    this.setState({
      videoUploadedBytes: (precentage / 100) * this.state.videoFileUploading.size,
    }, () => {
      if (this.state.uploadVideoForReplace) {
        this.setState({
          uploadVideoForReplace: {
            ...this.state.uploadVideoForReplace,
            uploadedBytes: this.state.videoUploadedBytes,
          },
        });
      }
    });
  };

  onUpdateUrl = (src) => {
    this.setState({
      videoObjFromUI: {
        url: src,
      },
    });
  };

  onPatchVideoObjFromMT = (videoObjFromMT) => {
    if (this.props.permissions.IsOriginalVideoPreviewPercentageEnabled) {
      const videoUrl = this.state.videoObjFromUI.url || videoObjFromMT.VideoBackupFileUrl;
      return {
        ...videoObjFromMT,
        DownloadUrl: videoObjFromMT.DownloadUrl || JSON.stringify({ 0: videoUrl }),
      };
    }

    return videoObjFromMT;
  };

  onFileRead = (file) => {
    const { repairMode, editingVideo } = this.state;

    const objURL = URL.createObjectURL(file);
    testThumbnail(objURL, 'file upload', this.props.activityLogger);
    this.onUpdateUrl(objURL);

    // Cache video type for replacing because we don't want to use updated value in our callbacks.
    const videoTypeReplacing = editingVideo && /* istanbul ignore next */editingVideo.Type;
    this.logToSubstrateAdvanced(`Start FileSize=${file.size}`, 'video upload');
    this.props.activityLogger.signal(fileExtension(file.name), 'video extension');

    const uploadPerfMarker = this.props.perfMarker.createChild('videoUpload');
    const getBlobPerfMarker = uploadPerfMarker.createChild('getBlob');
    const videoPreviewShowMarker = uploadPerfMarker.createChild('videoPreviewShowByFile');
    uploadPerfMarker.willInit();
    getBlobPerfMarker.willInit();
    videoPreviewShowMarker.willInit();
    this.setState({
      isVideoUploading: true,
      videoUploadedBytes: 0,
      videoFileUploading: file,
      uploadVideoForReplace: editingVideo ? {
        type: videoTypeReplacing,
        file,
        uploadedBytes: 0,
      } : null,
      isVideoSelectorDisplayed: false,
      errors: [],
    }, () => {
      this.props.onVideoUploadStatusUpdate(true);

      const self = this;

      handleFileRead(file, {
        repairMode,
        videoList: this.props.videoList,
        videoRepository: this.videoRepository,
        events: {
          onProgress: this.onProgress,
          onTokenUpdate: (token) => {
            getBlobPerfMarker.done();
            self.setState({
              uploadVideoAsyncToken: token,
            });
            return new Promise((resolve, reject) => {
              token.on({
                onSuccess: resolve,
                onError: reject,
                onCancel: () => {
                  /* istanbul ignore next */
                  self.logToSubstrateAdvanced(`Canceled FileSize=${file.size}`, 'video upload');
                  reject();
                },
              });
            });
          },
          onResponse: (response) => {
            self.logToSubstrateAdvanced(`Finish FileSize=${file.size}`, 'video upload');
            /* istanbul ignore if */
            if (repairMode) {
              self.logToSubstrateAdvanced('repair finished', 'video repair');
            }

            uploadPerfMarker.done({ fileSize: file.size });
            self.syncVideo(response.value, videoTypeReplacing, videoPreviewShowMarker);
          },
          onError: (error) => {
            if (error) {
              const errorMessage = FormatVideoError(error, this.props.i18n) ||/* istanbul ignore next */
                this.props.i18n.getString('ImageExperience_Video_UploadError_NoInternet');
              this.showErrors(errorMessage);
              /* istanbul ignore if */
              if (repairMode) {
                self.logToSubstrateAdvanced(`repair error: ${errorMessage}`, 'video repair');
              }
              this.logToSubstrateAdvanced(`Failed FileSize=${file.size} Error=${errorMessage.join('|')} Uploaded=${this.state.videoUploadedBytes}`, 'video upload');
            }
          },
          onFinally: () => {
            self.setState({
              isVideoUploading: false,
              videoFileUploading: null,
              uploadVideoAsyncToken: null,
              uploadVideoForReplace: null,
            });
            self.props.onVideoUploadStatusUpdate(false);
          },
        },
      });
    });
  };

  onVideoPostByUrl = (response, videoUrl) => {
    this.logToSubstrate('Video upload by url');
    this.onUpdateUrl(videoUrl);
    this.setState({
      isVideoSelectorDisplayed: false,
      errors: [],
    }, () => {
      const uploadPerfMarker = this.props.perfMarker.createChild('videoUpload');
      const videoPreviewShowMarker = uploadPerfMarker.createChild('videoPreviewShowByUrl');

      const videoTypeReplacing = this.state.editingVideo ? this.state.editingVideo.Type : null;
      this.syncVideo(response.value, videoTypeReplacing, videoPreviewShowMarker);
    });
  };

  onRepairProgress = (percentage) => {
    this.setState({
      repairProgress: percentage,
    });
  };

  getImagePathFromGenericImagePath =
    (genericImagePath) => {
      if (_.isString(genericImagePath)) {
        return genericImagePath.substr(0, genericImagePath.indexOf('1.91to1.jpg'));
      }

      return '';
    };

  handleVideoSelect = (idx) => {
    const { setSelectedVideoIdx } = this.props;
    setSelectedVideoIdx(idx);
  };

  shouldShowVideoReplacement() {
    const {
      permissions,
      videoList,
    } = this.props;

    const hasFailedvideo = _.some(videoList, vid => vid.status === 'Failed');

    return permissions.IsGoogleImportVideoAdsEnabled && hasFailedvideo;
  }

  /* istanbul ignore next */
  logToSubstrate(mes) {
    if (this.props.activityLogger) {
      this.props.activityLogger.signal(mes, 'video experience');
    }
  }

  /* istanbul ignore next */
  logToSubstrateAdvanced = (mes, module) => {
    if (this.props.activityLogger) {
      this.props.activityLogger.signal(mes, module);
    }
  };

  replaceVideo(videoObj) {
    if (this.props.multiVideoMode) {
      const newVideos = [...this.props.videoList];
      const index = _.findIndex(newVideos, video => video.idString === videoObj.idString);
      if (index !== -1) {
        newVideos[index] = videoObj;
      } else {
        newVideos.push(videoObj);
      }
      this.props.onVideoListUpdated(newVideos);
    } else {
      this.replaceSomeVideos([videoObj]);
    }
  }

  replaceSomeVideos(updatedVideoList) {
    const { videoList } = this.props;
    const newVideoList = _.isEmpty(videoList) ? updatedVideoList : [...videoList];

    if (newVideoList !== updatedVideoList) {
      newVideoList.forEach((video, index, _newVideoList) => {
        const matchVideo = _.find(updatedVideoList, updatedVideo => updatedVideo.Type === video.Type);
        if (matchVideo) {
          this.logToSubstrate(`Video replace: ${matchVideo.Type}`);
          // eslint-disable-next-line no-param-reassign
          _newVideoList[index] = matchVideo;
        }
      });
    }

    this.props.onVideoListUpdated(newVideoList);

    this.props.onVideoPreviewRequested(updatedVideoList[0].Type);
  }

  syncVideo = (idString, videoTypeReplacing, videoPreviewShowMarker) => {
    if (videoTypeReplacing) {
      this.syncVideoForType(idString, videoTypeReplacing, videoPreviewShowMarker);
    } else {
      this.syncVideoForAllTypes(idString, videoPreviewShowMarker);
    }
  };

  syncVideoForType = (idString, videoTypeReplacing, videoPreviewShowMarker) => {
    this.replaceVideo(newVideoFromMTObject({ SubType: videoTypeReplacing, Status: 'Draft', Id: idString }));
    this.videoSyncer.startSync({
      idString,
      shouldStop: () => {
        const video = this.props.videoList.find(v => v.Type === videoTypeReplacing);
        return video && video.IdString !== idString;
      },
      onUpdate: (videoFromMT) => {
        const videoObj = this.onPatchVideoObjFromMT(videoFromMT);

        if (videoObj.DownloadUrl && videoPreviewShowMarker) {
          videoPreviewShowMarker.done({ videoUrl: videoObj.Url, pilot: this.props.permissions.IsOriginalVideoPreviewPercentageEnabled });
        }

        this.replaceVideo(newVideoFromMTObject({ ...videoObj, SubType: videoTypeReplacing }));
      },
      onError: (error) => {
        /* istanbul ignore next */
        const errorMessage = FormatVideoError(error, this.props.i18n) ||
          this.props.i18n.getString('ImageExperience_Video_UploadError_NoInternet');
        this.showErrors(errorMessage);
        this.replaceVideo(newVideoFromMTObject({ SubType: videoTypeReplacing, Status: 'Failed', Id: idString }));
        this.logToSubstrateAdvanced(`Sync video failed: Error=${errorMessage.join('|')}, Type=${videoTypeReplacing}, Id=${idString}`, 'video sync');
      },
      activityLogger: this.props.activityLogger,
      perfMarker: this.props.perfMarker,
    });
  };

  syncVideoForMultipleTypes = (idString, videoTypes, videoPreviewShowMarker) => {
    const addedVideos = _.map(videoTypes, type => newVideoFromMTObject({ SubType: type, Status: 'Draft', Id: idString }));
    if (!this.props.multiVideoMode) {
      this.replaceSomeVideos(addedVideos);
    } else {
      this.props.onVideoListUpdated([...this.props.videoList, ...addedVideos]);
    }

    this.videoSyncer.startSync({
      idString,
      shouldStop: () => (this.props.videoList.length === 0 || this.props.videoList.every(v => v.IdString !== idString)),
      onUpdate: (videoFromMT) => {
        const videoObj = this.onPatchVideoObjFromMT(videoFromMT);

        if (videoObj.DownloadUrl && videoPreviewShowMarker) {
          videoPreviewShowMarker.done({ pilot: this.props.permissions.IsOriginalVideoPreviewPercentageEnabled });
        }

        if (this.props.videoList.length === 0) {
          return;
        }
        const newVideos = [];
        this.props.videoList.forEach((video) => {
          if (video.IdString === idString) {
            newVideos.push(newVideoFromMTObject({ ...videoObj, SubType: video.Type }));
          } else {
            newVideos.push(video);
          }
        });
        this.props.onVideoListUpdated(newVideos);
      },
      onError: (error) => {
        /* istanbul ignore next */
        const errorMessage = FormatVideoError(error, this.props.i18n) ||
          this.props.i18n.getString('ImageExperience_Video_UploadError_NoInternet');
        this.showErrors(errorMessage);
        this.logToSubstrateAdvanced(`Sync video for all types failed: Error=${errorMessage.join('|')} Id=${idString}`, 'video sync');
      },
      activityLogger: this.props.activityLogger,
      perfMarker: this.props.perfMarker,
    });
  };

  syncVideoForAllTypes = (idString, videoPreviewShowMarker) => this.syncVideoForMultipleTypes(idString, this.allowedVideoSizes, videoPreviewShowMarker);

  showErrors = (errors) => {
    this.setState({ errors });

    // indicate to parent component that this componenet has errors.
    this.props.onErrorsUpdated(errors);
  };

  videoListComponent() {
    const { CustomAddVideoControl, selectedVideoIdx } = this.props;

    const errors = this.state.errors.map((errMsg, index) => (
      <div
        className="error"
        // eslint-disable-next-line react/no-array-index-key
        key={`error_${index}`}
      >
        {errMsg}
      </div>
    ));

    if (this.props.multiVideoMode) {
      return (
        <MultiVideoListView
          videoList={this.props.videoList}
          i18n={this.props.i18n}
          onCancelUpload={this.onCancelUpload}
          isVideoUploading={this.state.isVideoUploading}
          totalBytes={this.state.videoFileUploading && this.state.videoFileUploading.size}
          uploadedBytes={this.state.videoUploadedBytes}
          selectedVideoIdx={selectedVideoIdx}
          onVideoSelect={this.handleVideoSelect}
          CustomAddVideoControl={CustomAddVideoControl}
          onRenderItemDropdown={this.props.onRenderItemDropdown}
          onAddVideo={this.onAddVideo}
        />
      );
    }

    if (this.props.videoList.length > 0) {
      return (
        <React.Fragment>
          <VideoListView
            videoList={this.props.videoList}
            customThumbnailRecord={this.props.customThumbnailRecord}
            i18n={this.props.i18n}
            onReplace={this.onReplaceVideo}
            onEdit={this.onEditVideo}
            onRemoveAll={this.onRemoveAllVideos}
            onCancelUpload={this.onCancelUpload}
            uploadVideo={this.state.uploadVideoForReplace}
            permissions={this.props.permissions}
            cid={this.props.cid}
            aid={this.props.aid}
            isLazyLoadEnabled={this.props.isLazyLoadEnabled}
            perfMarker={this.props.perfMarker.createChild('videoPicker')}
            onVideoSelectedReplace={this.onVideoSelectedReplace}
            videoRepository={this.videoRepository}
            activityLogger={this.props.activityLogger}
            oDataConfig={this.props.oDataConfig}
            onVideoListUpdated={this.props.onVideoListUpdated}
            allowedVideoSizes={this.props.allowedVideoSizes}
            onRenderItemDropdown={this.props.onRenderItemDropdown}
            disableRemoveAll={this.props.disableRemoveAll}
            hideRatioLabel={this.props.hideRatioLabel}
          />
          {this.props.enableShowingErrors && errors}
        </React.Fragment>
      );
    }

    return (
      <div>
        {!this.props.multiVideoMode &&
          <div className="image-section-label">
            <label>
              {this.props.i18n.getString('ImageExperience_Video_Label')}
              <HelpPopupInline
                topic={this.props.videoLabelHelpTopic || 'pop_BA_VideoAds_VideoSource'}
                aria-label={this.props.i18n.getString('Click to learn more about {{item}}', { item: this.props.i18n.getString('Import_ImportSummary_Videos') })}
                ariaLabel={this.props.i18n.getString('Click to learn more about {{item}}', { item: this.props.i18n.getString('Import_ImportSummary_Videos') })}
              />
            </label>
          </div>}
        {
          this.state.isVideoUploading && (
            <div className={classNames(['col-sm-3', 'image-list-item-status-indicator'])}>
              {
                !this.props.disableUploadTextField && (<TextField
                  placeholder={this.props.i18n.getString('ImageExperience_Add_Video_File_Placeholder')}
                  value={this.state.videoFileUploading ? this.state.videoFileUploading.name : /* istanbul ignore next */''}
                  readOnly
                  className="file-picker-input"
                />)
              }
              <UploadProgress
                totalBytes={this.state.videoFileUploading.size}
                uploadedBytes={this.state.videoUploadedBytes}
                i18n={this.props.i18n}
                type={this.props.videoExperienceCircleUploadType ? 'circle' : 'bar'}
              />
              <DefaultButton
                ariaLabel={`${this.props.i18n.getString('Audience_Campaign_Cancel_Label')} ${this.props.i18n.getString('ImageExperience_Upload')}`}
                text={this.props.i18n.getString('Audience_Campaign_Cancel_Label')}
                onClick={this.onCancelUpload}
              />
            </div>)
        }
        {
          !this.state.isVideoUploading && CustomAddVideoControl && <CustomAddVideoControl onClick={this.onAddVideo} multiVideoMode={this.props.multiVideoMode} />
        }
        {
          !this.state.isVideoUploading && !CustomAddVideoControl && (
            <button
              className="add-image-button btn btn-hero"
              onClick={this.onAddVideo}
            >
              {this.props.i18n.getString('ImageExperience_LoadVideo')}
            </button>)
        }
        { // offload showing error to parent component
          this.props.enableShowingErrors && errors
        }
      </div>
    );
  }

  repairVideo() {
    const {
      classes, i18n, videoList, permissions,
    } = this.props;
    const { isVideoUploading, repairProgress } = this.state;
    const videoThumbnail = _.find(videoList, _video => _video.status === 'Failed');

    const shouldRepairGoogleImportVideoAd = permissions && permissions.IsGoogleImportVideoAdsEnabled
      && _.isArray(videoList) && videoList.length === 1
      && videoList[0].sourceType === 'GoogleImported'
      && videoList[0].status === 'Failed';

    if (shouldRepairGoogleImportVideoAd) {
      return (
        <div className="replace-video-container">
          <div className={`placeholder-alert-container ${classes.root}`}>
            <div className="placeholder-contents">
              <div className="placeholder-body">
                <div className="place-holder-img-container">
                  <img className="placeholder-video-replace-img" src={GenericImage} alt={i18n.getString(_TL_('Replace Video'))} />
                </div>
                <div className="placeholder-action">
                  <div className="placeholder-alert-header">
                    <div className="placeholder-alert-title">{i18n.getString(_TL_('Replace Your Video'))}</div>
                  </div>
                  <ul className="placeholder-video-info">
                    <li>
                      <span className="placeholder-list-name">{i18n.getString(_TL_('Video Name:'))}</span>{`  ${videoList[0].description}`}
                    </li>
                    <li>
                      <span className="placeholder-list-name">{i18n.getString(_TL_('Invalid Video Url:'))}</span>
                      <a href={videoList[0].sourceUrl}>{videoList[0].sourceUrl}</a>
                      <button className="placeholder-copy-link" onClick={() => navigator.clipboard.writeText(videoList[0].sourceUrl)}>
                        <div>{i18n.getString(_TL_('Copy Link'))}</div>
                        <div className="url-copied">{i18n.getString(_TL_('URL Copied!'))}</div>
                      </button>
                    </li>
                  </ul>
                  {videoThumbnail.status === 'Failed' && !isVideoUploading &&
                    <div>
                      <button onClick={this.onRepairVideo} className="btn btn-hero replace-btn">{i18n.getString(_TL_('Replace Video'))}</button>
                      <div className="google-import-video-ad-message">
                        <MessageBar>
                          {i18n.getString(_TL_('We can’t use a YouTube video for a video campaign. You need to upload a video file yourself.'))}
                        </MessageBar>
                      </div>
                    </div>
                  }
                  {isVideoUploading &&
                    <ProgressBar
                      i18n={i18n}
                      uploadpercentage={repairProgress}
                      onCancelUpload={this.onCancelUpload}
                    />}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="replace-video-container">
        <div className={`placeholder-alert-container ${classes.root}`}>
          <div className="placeholder-contents">
            <div className="placeholder-body">
              <div className="place-holder-img-container">
                <img className="placeholder-video-replace-img" src={GenericImage} alt={i18n.getString(_TL_('Replace Video'))} />
              </div>
              <div className="placeholder-action">
                <div className="placeholder-alert-header">
                  <div className="placeholder-alert-title">{i18n.getString(_TL_('Replace Your Video'))}</div>
                </div>
                <ul className="placeholder-video-info">
                  <li>
                    <span className="placeholder-list-name">{i18n.getString(_TL_('Video Name:'))}</span>{`  ${videoThumbnail.text}`}
                  </li>
                  <li>
                    <span className="placeholder-list-name">{i18n.getString(_TL_('Invalid Video Url:'))}</span>
                    <a href={videoThumbnail.srcUrl}>{videoThumbnail.srcUrl}</a>
                    <button className="placeholder-copy-link" onClick={() => navigator.clipboard.writeText(videoThumbnail.srcUrl)}>
                      <div>{i18n.getString(_TL_('Copy Link'))}</div>
                      <div className="url-copied">{i18n.getString(_TL_('URL Copied!'))}</div>
                    </button>
                  </li>
                </ul>
                {videoThumbnail.status === 'Failed' && !isVideoUploading &&
                  <button onClick={this.onRepairVideo} className="btn btn-hero replace-btn">{i18n.getString(_TL_('Replace Video'))}</button>
                }
                {isVideoUploading &&
                  <ProgressBar
                    i18n={i18n}
                    uploadpercentage={repairProgress}
                    onCancelUpload={this.onCancelUpload}
                  />}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  fetchAccountData = () => this.jsDataCcRest.Account.find(this.props.aid)
    .then((data) => {
      if (this.isMountedFlag) {
        const isEnabled = isValidXandrAccount(data);
        this.setState({ isXandrIntegrationEnabled: isEnabled });
      }
    })
    .catch(() => {
      this.showErrors(this.props.i18n.getString('ImageExperience_Account_Fetch_Error'));
    });

  render() {
    const {
      i18n,
      cid,
      aid,
      isLazyLoadEnabled,
      perfMarker,
      permissions,
      genericImage,
      customThumbnailRecord,
      videoList,
    } = this.props;

    return (
      <div className="image-experience-content">
        {this.shouldShowVideoReplacement() ? this.repairVideo() : this.videoListComponent()}
        <AddVideoContainer
          isOpen={this.state.isVideoSelectorDisplayed}
          isReplace={this.state.editingVideo !== null}
          onVideoSelected={this.onVideoSelected}
          onSelectionCanceled={this.onVideoSelectionCanceled}
          i18n={i18n}
          perfMarker={perfMarker.createChild('videoPicker')}
          cid={cid}
          aid={aid}
          isXandrIntegrationEnabled={this.state.isXandrIntegrationEnabled}
          videoRepository={this.videoRepository}
          isLazyLoadEnabled={isLazyLoadEnabled}
          videoFile={this.state.videoFileUploading}
          isVideoUploading={this.state.isVideoUploading}
          videoUploadedBytes={this.state.videoUploadedBytes}
          onFileRead={this.onFileRead}
          logToSubstrateAdvanced={this.logToSubstrateAdvanced}
          repairMode={this.state.repairMode}
          onCancelUpload={this.onCancelUpload}
          onVideoPostByUrl={this.onVideoPostByUrl}
          videoList={videoList}
          videoPickerContainerHeight={this.props.videoPickerContainerHeight}
        />
        {
          permissions.IsVideoAdEditorEnabled &&
          this.state.isEditVideoDisplayed &&
          <EditVideo
            {...this.props}
            onSaveThumbnail={this.onSaveThumbnail}
            onCropped={this.onCropped}
            onClipped={this.onClipped}
            onDismiss={() => this.setState({ isEditVideoDisplayed: false })}
            thumbnailRecord={customThumbnailRecord}
            i18n={i18n}
            perfMarker={perfMarker.createChild('editVideo')}
            video={this.state.editingVideo}
            staticImagePath={this.getImagePathFromGenericImagePath(genericImage)}
            videoRepository={this.videoRepository}
            resourceRepository={this.videoResRepository}
          />
        }
      </div>
    );
  }
}

VideoExperienceComponent.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  genericImage: PropTypes.string,
  videoList: PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceVideo)),
  customThumbnailRecord: CustomThumbnailRecordType,
  onVideoListUpdated: PropTypes.func,
  onVideoPreviewRequested: PropTypes.func,
  onSaveThumbnailAsDraft: PropTypes.func,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({}),
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
    applicationName: PropTypes.string,
    oDataTokenType: PropTypes.string,
  }),
  isLazyLoadEnabled: PropTypes.bool,
  isStockPhotoEnabled: PropTypes.bool,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func.isRequired,
  }),
  onVideoUploadStatusUpdate: PropTypes.func,
  permissions: PropTypes.shape({
    IsStockPhotoFilterEnabled: PropTypes.bool,
    IsVideoAdEditorEnabled: PropTypes.bool,
    IsVideoAdEditorCropClipEnabled: PropTypes.bool,
    IsVideoAdEditorThumbnailEnabled: PropTypes.bool,
    IsVideoAdEditorCaptionEnabled: PropTypes.bool,
    IsOriginalVideoPreviewPercentageEnabled: PropTypes.bool,
    dynamic: PropTypes.shape({ XandrIntegrationEnabled: PropTypes.bool }),
  }),
  allowedVideoSizes: PropTypes.arrayOf(PropTypes.string),
  onRenderItemDropdown: PropTypes.func,
  multiVideoMode: PropTypes.bool,
  disableRemoveAll: PropTypes.bool,
  hideRatioLabel: PropTypes.bool,
  videoPickerContainerHeight: PropTypes.number,
  videoExperienceCircleUploadType: PropTypes.bool,
  disableUploadTextField: PropTypes.bool,
  onErrorsUpdated: PropTypes.func,
  enableShowingErrors: PropTypes.bool,
  videoLabelHelpTopic: PropTypes.bool,
  CustomAddVideoControl: PropTypes.elementType,
  serverContext: PropTypes.shape({
    CurrentUser: PropTypes.shape({
      Id: PropTypes.number,
    }),
  }),
};

VideoExperienceComponent.defaultProps = {
  onVideoListUpdated: _.noop,
  onVideoPreviewRequested: _.noop,
  onSaveThumbnailAsDraft: _.noop,
  oDataConfig: null,
  classes: {},
  isLazyLoadEnabled: true,
  isStockPhotoEnabled: true,
  activityLogger: {
    signal: _.noop,
  },
  genericImage: null,
  videoList: [],
  customThumbnailRecord: {},
  onVideoUploadStatusUpdate: _.noop,
  permissions: { dynamic: {} },
  allowedVideoSizes: null,
  onRenderItemDropdown: null,
  disableRemoveAll: false,
  hideRatioLabel: false,
  multiVideoMode: false,
  videoPickerContainerHeight: null,
  videoExperienceCircleUploadType: false,
  disableUploadTextField: false,
  onErrorsUpdated: _.noop,
  enableShowingErrors: true,
  videoLabelHelpTopic: null,
  CustomAddVideoControl: null,
  serverContext: {
    CurrentUser: { Id: null },
  },
};

const VideoExperience = withDefaultStyles(withPerfMarker(VideoExperienceComponent), getReplaceVideoStyle);

export { VideoExperience };
